<template>
  <v-dialog :value="value" max-width="1100" :fullscreen="$vuetify.breakpoint.xsOnly" scrollable :persistent="saving" @input="close">
    <v-card dark class="export-editor">
      <v-card-title class="export-heading">
        <div class="export-heading-copy">
          <h1>{{ formatLabel }} bearbeiten</h1>
          <p v-if="plan" class="export-note">{{ plan.studioName }} · {{ formatDate(plan.start) }} – {{ formatDate(plan.end) }}</p>
          <p class="export-hint">Änderungen gelten nur für diesen Download.</p>
        </div>
        <v-btn icon :disabled="saving" aria-label="Bearbeitungsmodus schliessen" @click="close"><v-icon>mdi-close</v-icon></v-btn>
      </v-card-title>

      <div v-if="plan && !loading && rows.length" class="export-toolbar">
        <div class="export-filters">
          <v-text-field
            v-model="filterText"
            label="Kurse im Export suchen"
            prepend-inner-icon="mdi-magnify"
            clearable
            outlined
            dense
            hide-details
            class="export-search"
            :disabled="saving"></v-text-field>
          <v-checkbox v-model="onlyChanged" :label="`Nur Änderungen (${changedCount})`" hide-details :disabled="saving" class="export-change-filter"></v-checkbox>
        </div>
        <div v-if="days.length > 1" class="export-day-nav" role="group" aria-label="Tag auswählen">
          <v-btn :color="selectedDay === 'all' ? 'primary' : undefined" :outlined="selectedDay !== 'all'" :aria-pressed="selectedDay === 'all'" :disabled="saving" depressed class="export-day-button" @click="selectedDay = 'all'">
            Alle Tage <span class="export-day-count">{{ rows.length }}</span>
          </v-btn>
          <v-btn
            v-for="day in days"
            :key="day.date"
            :color="selectedDay === day.date ? 'primary' : undefined"
            :outlined="selectedDay !== day.date"
            :aria-pressed="selectedDay === day.date"
            :disabled="saving"
            depressed
            class="export-day-button"
            @click="selectedDay = day.date">
            {{ shortDayLabel(day.date) }} <span class="export-day-count">{{ day.rows.length }}</span>
          </v-btn>
        </div>
      </div>
      <v-divider></v-divider>

      <v-card-text class="export-content">
        <v-progress-linear v-if="loading" indeterminate color="primary" aria-label="Kurse werden geladen"></v-progress-linear>
        <v-alert v-if="error" type="error" text>{{ error }}</v-alert>
        <v-btn v-if="!loading && !plan" outlined @click="loadPlan">Erneut laden</v-btn>
        <template v-if="plan && !loading">
          <v-alert v-if="!rows.length" type="info" text>Für diesen Zeitraum sind keine Kurse im Export enthalten.</v-alert>
          <div v-else-if="!filteredDays.length" class="export-empty">
            <v-icon color="grey">mdi-filter-outline</v-icon>
            <p>Keine Kurse für diese Auswahl.</p>
            <v-btn outlined class="export-text-button" @click="clearFilters">Alle Kurse anzeigen</v-btn>
          </div>
          <section v-for="day in filteredDays" :key="day.date" class="export-day">
            <div class="export-day-heading">
              <h2>{{ dayLabel(day.date) }}</h2>
              <span class="export-note">{{ day.rows.length }} {{ day.rows.length === 1 ? 'Kurs' : 'Kurse' }}</span>
            </div>
            <div class="export-day-list">
              <div v-for="row in day.rows" :key="row.key" class="export-row" :class="{ 'is-open': openKey === row.key, 'is-changed': isChanged(row) }">
                <button
                  type="button"
                  class="export-row-toggle"
                  :aria-expanded="openKey === row.key"
                  :aria-controls="`export-fields-${row.key}`"
                  :aria-label="`${row.time}, ${row.title || row.originalTitle}, ${openKey === row.key ? 'Bearbeitung schliessen' : 'bearbeiten'}`"
                  :disabled="saving"
                  @click="openKey = openKey === row.key ? null : row.key">
                  <span class="export-row-time">{{ row.time }}</span>
                  <span class="export-row-name" :class="{ 'export-muted': !row.normal, 'export-bold': row.bold }">{{ row.title || row.originalTitle }}</span>
                  <span class="export-row-badges">
                    <span v-if="!row.title.trim()" class="export-badge export-badge-error">Beschriftung fehlt</span>
                    <span v-else-if="isChanged(row)" class="export-badge export-badge-changed">Geändert</span>
                    <span v-if="row.cancelled" class="export-badge">{{ row.normal ? 'Abgesagt · schwarz' : 'Abgesagt · matt' }}</span>
                  </span>
                  <v-icon aria-hidden="true">{{ openKey === row.key ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                </button>

                <div v-if="openKey === row.key" :id="`export-fields-${row.key}`" class="export-row-fields">
                  <div class="export-mode" role="group" aria-label="Beschriftung auswählen">
                    <v-btn :outlined="row.custom" :color="!row.custom ? 'primary' : undefined" :aria-pressed="!row.custom" :disabled="saving" depressed class="export-text-button" @click="row.custom && useCourseSearch(row)">
                      <v-icon left>mdi-magnify</v-icon>Kurssuche
                    </v-btn>
                    <v-btn :outlined="!row.custom" :color="row.custom ? 'primary' : undefined" :aria-pressed="row.custom" :disabled="saving" depressed class="export-text-button" @click="!row.custom && useCustomTitle(row)">
                      <v-icon left>mdi-pencil-outline</v-icon>Eigener Text
                    </v-btn>
                  </div>
                  <v-autocomplete
                    v-if="!row.custom"
                    v-model="row.courseId"
                    :items="courseItems(row)"
                    :search-input.sync="row.search"
                    item-text="name"
                    item-value="_id"
                    label="Kurs suchen und auswählen"
                    outlined
                    dense
                    hide-details="auto"
                    :disabled="saving"
                    :error-messages="!row.title ? ['Bitte einen Kurs auswählen oder eigenen Text eingeben.'] : []"
                    @change="selectCourse(row)">
                    <template v-slot:no-data><div class="pa-4 export-note">Kein passender Kurs gefunden. Du kannst eigenen Text verwenden.</div></template>
                    <template v-slot:append-item>
                      <v-divider></v-divider>
                      <v-list-item @mousedown.prevent @click="useCustomTitle(row)">
                        <v-list-item-icon><v-icon>mdi-pencil-outline</v-icon></v-list-item-icon>
                        <v-list-item-content><v-list-item-title>Eigenen Text verwenden</v-list-item-title></v-list-item-content>
                      </v-list-item>
                    </template>
                  </v-autocomplete>
                  <v-text-field
                    v-else
                    v-model="row.title"
                    label="Beschriftung im Export"
                    outlined
                    dense
                    counter="120"
                    maxlength="120"
                    :disabled="saving"
                    :rules="titleRules"></v-text-field>
                  <p v-if="isTitleChanged(row)" class="export-note export-original">Ursprünglich: {{ row.originalTitle }}</p>
                  <div class="export-format-options">
                    <v-switch v-model="row.bold" label="Fett darstellen" color="primary" hide-details :disabled="saving"></v-switch>
                    <v-switch v-if="row.cancelled" v-model="row.normal" label="Schwarz statt matt" color="primary" hide-details :disabled="saving"></v-switch>
                  </div>
                  <div class="export-row-actions">
                    <v-btn v-if="isChanged(row)" text class="export-text-button" :disabled="saving" @click="resetRow(row)">
                      <v-icon left>mdi-restore</v-icon>Zurücksetzen
                    </v-btn>
                    <v-spacer></v-spacer>
                    <v-btn outlined class="export-text-button" :disabled="saving" @click="openKey = null">Fertig</v-btn>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </template>
      </v-card-text>
      <v-divider></v-divider>
      <v-card-actions class="export-actions">
        <span v-if="plan" class="export-note export-summary" aria-live="polite">
          {{ rows.length }} Kurse im Export · {{ changedCount ? `${changedCount} angepasst` : 'Keine Anpassungen' }}
        </span>
        <v-spacer></v-spacer>
        <v-btn text :disabled="saving" @click="close">Abbrechen</v-btn>
        <v-btn color="primary" :loading="saving" :disabled="loading || !canDownload" @click="download">
          <v-icon left>mdi-download</v-icon>{{ formatLabel }} herunterladen
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import api from "../../../api"

export default {
  name: "GroupFitnessExportEditor",
  props: {
    value: Boolean,
    studio: { type: String, default: "" },
    options: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      loading: false,
      saving: false,
      error: "",
      plan: null,
      rows: [],
      courses: [],
      loadId: 0,
      filterText: "",
      onlyChanged: false,
      selectedDay: "all",
      openKey: null,
      titleRules: [(value) => Boolean((value || "").trim()) || "Bitte eine Beschriftung eingeben.", (value) => (value || "").length <= 120 || "Maximal 120 Zeichen."],
    }
  },
  computed: {
    formatLabel() {
      return { pdf: "PDF-Wochenplan", day: "Tagesbild", week: "Wochen-ZIP" }[this.options.format] || "Export"
    },
    days() {
      const days = []
      this.rows.forEach((row) => {
        let day = days.find((item) => item.date === row.date)
        if (!day) {
          day = { date: row.date, rows: [] }
          days.push(day)
        }
        day.rows.push(row)
      })
      return days
    },
    filteredDays() {
      const query = this.normalizeTitle(this.filterText).toLocaleLowerCase("de")
      return this.days
        .filter((day) => this.selectedDay === "all" || day.date === this.selectedDay)
        .map((day) => ({
          date: day.date,
          rows: day.rows.filter((row) => {
            if (this.onlyChanged && !this.isChanged(row)) return false
            const text = this.normalizeTitle(`${row.title} ${row.originalTitle} ${row.time}`).toLocaleLowerCase("de")
            return !query || text.includes(query)
          }),
        }))
        .filter((day) => day.rows.length)
    },
    changedCount() {
      return this.rows.filter((row) => this.isChanged(row)).length
    },
    canDownload() {
      return Boolean(this.plan && this.rows.length && this.rows.every((row) => row.title.trim().length > 0 && row.title.length <= 120))
    },
  },
  watch: {
    value(open) {
      if (open) this.loadPlan()
      else {
        this.loadId++
        this.plan = null
        this.rows = []
        this.courses = []
        this.error = ""
        this.clearFilters()
      }
    },
    filterText() { this.openKey = null },
    onlyChanged() { this.openKey = null },
    selectedDay() { this.openKey = null },
  },
  beforeDestroy() { this.loadId++ },
  methods: {
    async loadPlan() {
      const loadId = ++this.loadId
      this.loading = true
      this.error = ""
      this.plan = null
      this.rows = []
      this.clearFilters()
      try {
        const plan = await api.fetchGFPlanEditor(this.studio, this.options)
        if (loadId !== this.loadId || !this.value) return
        this.plan = plan
        this.courses = plan.courses
        this.rows = plan.rows.map((row) => ({
          ...row,
          bold: row.bold !== false,
          originalTitle: row.title,
          originalCourseId: row.courseId,
          originalNormal: row.normal,
          originalBold: row.bold !== false,
          custom: !row.courseId,
          search: null,
        }))
      } catch (error) {
        if (loadId === this.loadId) this.error = typeof error.response?.data === "string" ? error.response.data : "Die Kurse konnten nicht geladen werden. Bitte versuche es erneut."
      } finally {
        if (loadId === this.loadId) this.loading = false
      }
    },
    courseItems(row) {
      if (!row.originalCourseId || this.courses.some((course) => course._id === row.originalCourseId)) return this.courses
      return [{ _id: row.originalCourseId, name: row.originalTitle }, ...this.courses]
    },
    selectCourse(row) {
      const course = this.courseItems(row).find((item) => item._id === row.courseId)
      row.title = course ? course.name : ""
      row.search = null
    },
    useCustomTitle(row) {
      row.title = (row.search || row.title || row.originalTitle).trim()
      row.custom = true
      row.courseId = null
      row.search = null
    },
    useCourseSearch(row) {
      row.custom = false
      row.courseId = null
      row.title = ""
      row.search = null
    },
    normalizeTitle(value) {
      return String(value || "").replace(/\s+/g, " ").trim()
    },
    isTitleChanged(row) {
      return this.normalizeTitle(row.title) !== this.normalizeTitle(row.originalTitle)
    },
    isChanged(row) {
      return this.isTitleChanged(row) || row.normal !== row.originalNormal || row.bold !== row.originalBold
    },
    clearFilters() {
      this.filterText = ""
      this.onlyChanged = false
      this.selectedDay = "all"
      this.openKey = null
    },
    resetRow(row) {
      row.title = row.originalTitle
      row.courseId = row.originalCourseId
      row.normal = row.originalNormal
      row.bold = row.originalBold
      row.custom = !row.originalCourseId
      row.search = null
    },
    formatDate(date) { return this.$moment(date).format("DD.MM.YYYY") },
    dayLabel(date) { return this.$moment(date).locale("de").format("dddd, DD.MM.YYYY") },
    shortDayLabel(date) { return this.$moment(date).locale("de").format("dd, DD.MM.") },
    close() { if (!this.saving) this.$emit("input", false) },
    async download() {
      if (!this.canDownload || this.saving) return
      this.saving = true
      this.error = ""
      try {
        const { format, start, end, showDates } = this.plan
        const overrides = this.rows.filter((row) => this.isChanged(row)).map((row) => ({
          key: row.key,
          title: this.normalizeTitle(row.title),
          normal: row.normal,
          bold: row.bold,
        }))
        await api.downloadGFEditedPlan(this.studio, { format, start, end, showDates, overrides })
        this.$toast.success("Export wurde erstellt")
        this.$emit("input", false)
      } catch (error) {
        this.error = error.message || "Der Export konnte nicht erstellt werden. Deine Anpassungen sind weiterhin vorhanden."
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.export-editor { font-size: 1rem; }
.export-heading { align-items: flex-start; justify-content: space-between; gap: 0.75rem; padding: 1rem 1.25rem 0.875rem; }
.export-heading-copy { min-width: 0; }
.export-heading h1 { margin: 0; font-size: 1.25rem; font-weight: 600; line-height: 1.5; word-break: normal; }
.export-heading p { margin: 0.25rem 0 0; }
.export-heading > .v-btn { flex-shrink: 0; }
.export-note { font-size: 0.875rem; font-weight: 400; line-height: 1.5; overflow-wrap: anywhere; }
.export-hint { color: #bdbdbd; font-size: 0.875rem; line-height: 1.5; }
.export-toolbar { flex: 0 0 auto; padding: 0 1.25rem 0.75rem; }
.export-filters { display: flex; align-items: center; flex-wrap: wrap; gap: 0.75rem 1.25rem; }
.export-search { flex: 1 1 16rem; min-width: 0; }
.export-change-filter { margin: 0; padding: 0; flex: 0 0 auto; }
.export-day-nav { display: flex; gap: 0.5rem; margin-top: 0.75rem; padding: 0.125rem 0.125rem 0.5rem; overflow-x: auto; }
.export-day-button { flex: 0 0 auto; font-size: 0.875rem; letter-spacing: normal; text-transform: none; }
.export-day-count { margin-left: 0.5rem; font-size: 0.75rem; }
.export-content { padding: 0.5rem 1.25rem 1rem !important; }
.export-day-heading { display: flex; justify-content: space-between; align-items: baseline; gap: 0.75rem; margin: 0.875rem 0 0.5rem; }
.export-day-heading h2 { margin: 0; font-size: 1rem; font-weight: 600; line-height: 1.5; }
.export-day-heading > span { flex-shrink: 0; color: #bdbdbd; }
.export-day-list { border: 1px solid #454545; border-radius: 0.625rem; }
.export-row + .export-row { border-top: 1px solid #3d3d3d; }
.export-row { border-left: 3px solid transparent; }
.export-row:first-child { border-radius: 0.5rem 0.5rem 0 0; }
.export-row:last-child { border-radius: 0 0 0.5rem 0.5rem; }
.export-row:only-child { border-radius: 0.5rem; }
.export-row.is-changed { border-left-color: #a6d9b2; }
.export-row.is-open { background: #252b27; }
.export-row-toggle { display: grid; grid-template-columns: 8.5rem minmax(0, 1fr) auto 1.5rem; gap: 0.75rem; align-items: center; width: 100%; min-height: 3.5rem; padding: 0.75rem 0.875rem; border-radius: inherit; color: inherit; text-align: left; }
.export-row-toggle:hover:not(:disabled) { background: rgba(255, 255, 255, 0.04); }
.export-row-toggle:focus-visible { outline: 2px solid #a6d9b2; outline-offset: -2px; }
.export-row-toggle:disabled { cursor: default; }
.export-row-time { font-size: 0.875rem; font-weight: 500; font-variant-numeric: tabular-nums; }
.export-row-name { min-width: 0; font-size: 1rem; font-weight: 400; line-height: 1.5; overflow-wrap: anywhere; }
.export-row-name.export-bold { font-weight: 700; }
.export-muted { color: #b0b0b0; }
.export-row-badges { display: flex; gap: 0.375rem; flex-wrap: wrap; justify-content: flex-end; }
.export-badge { padding: 0.125rem 0.5rem; border: 1px solid #777; border-radius: 0.375rem; font-size: 0.75rem; font-weight: 400; line-height: 1.5; }
.export-badge-changed { border-color: #a6d9b2; color: #c7e9cf; }
.export-badge-error { border-color: #f4a5a5; color: #ffc2c2; }
.export-row-fields { padding: 0.25rem 1rem 1rem 10.125rem; }
.export-mode { display: flex; flex-wrap: wrap; gap: 0.5rem; margin-bottom: 1rem; }
.export-text-button { font-size: 0.875rem; text-transform: none; letter-spacing: normal; }
.export-original { margin: 0.5rem 0 0; color: #bdbdbd; }
.export-format-options { display: flex; flex-wrap: wrap; gap: 0.75rem 1.5rem; margin-top: 0.875rem; }
.export-format-options .v-input { margin: 0; padding: 0; }
.export-row-actions { display: flex; gap: 0.5rem; flex-wrap: wrap; align-items: center; margin-top: 0.875rem; }
.export-empty { padding: 2rem 1rem; text-align: center; }
.export-empty p { margin: 0.75rem 0; font-size: 1rem; }
.export-actions { gap: 0.5rem; flex-wrap: wrap; padding: 1rem 1.25rem; }
.export-actions .v-btn { margin-left: 0 !important; font-size: 0.875rem; letter-spacing: normal; text-transform: none; }
::v-deep .v-label, ::v-deep input, ::v-deep .v-select__selection { font-size: 1rem; }
::v-deep .v-messages, ::v-deep .v-counter { font-size: 0.75rem; }
.export-change-filter ::v-deep .v-label { font-size: 0.875rem; }
@media (max-width: 759px) {
  .export-row-toggle { grid-template-columns: 7.5rem minmax(0, 1fr) 1.5rem; gap: 0.375rem 0.75rem; }
  .export-row-badges { grid-column: 2; justify-content: flex-start; }
  .export-row-badges:empty { display: none; }
  .export-row-toggle > .v-icon { grid-column: 3; grid-row: 1; }
  .export-row-fields { padding-left: 1rem; }
  .export-summary { flex-basis: 100%; }
}
@media (max-width: 479px) {
  .export-heading, .export-actions { padding: 0.875rem; }
  .export-toolbar { padding-right: 0.875rem; padding-left: 0.875rem; }
  .export-content { padding-right: 0.875rem !important; padding-left: 0.875rem !important; }
  .export-heading h1 { font-size: 1.125rem; }
  .export-row-toggle { grid-template-columns: minmax(0, 1fr) 1.5rem; }
  .export-row-time { grid-column: 1; }
  .export-row-name { grid-column: 1; }
  .export-row-badges { grid-column: 1; }
  .export-row-toggle > .v-icon { grid-column: 2; }
  .export-actions > .v-btn { width: 100%; }
}
</style>
